import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type SpeechCalibrationTrait = 'clarity' | 'pronunciation' | 'fluency' | 'prosody';
export type SpeechCalibrationConfidence = 'low' | 'medium' | 'high';
export type SpeechCalibrationVerdict =
    | 'much_too_low'
    | 'too_low'
    | 'about_right'
    | 'too_high'
    | 'much_too_high'
    | 'cannot_judge';
export type SpeechCalibrationPhoneVerdict =
    | 'correct'
    | 'wrong'
    | 'valid_accent_variant'
    | 'not_sure';

export type SpeechCalibrationCapabilities = {
    dailyTarget: number;
    traitScale: { min: number; max: number; step: number };
    traits: SpeechCalibrationTrait[];
    blindReviewRequired: boolean;
    independentRatersRequired: number;
    statuses: string[];
};

export type SpeechCalibrationQueueItem = {
    assignmentId: string;
    queueDate: string;
    status: string;
    examName: string;
    examTaskType: string;
    questionNo: string | null;
    durationSeconds: number | null;
    selectionReasons: string[];
    assignedReviewerId: string | null;
    completedAtUtc: string | null;
};

export type SpeechCalibrationDailyQueue = {
    date: string;
    summary: {
        target: number;
        total: number;
        pending: number;
        blindRated: number;
        compared: number;
        needsAdjudication: number;
        unusable: number;
    };
    items: SpeechCalibrationQueueItem[];
    page: number;
    pageSize: number;
    total: number;
    createdCount?: number;
};

export type SpeechCalibrationBlindReview = {
    assignmentId: string;
    practiceExamTaskAttemptId: string;
    audioUrl: string;
    referenceText: string | null;
    examName: string;
    examTaskType: string;
    questionNo: string | null;
    durationSeconds: number | null;
    flags: string[];
};

export type SpeechCalibrationComparison = {
    assignmentId: string;
    modelRevision: string;
    schemaVersion: string;
    traits: Array<{
        trait: SpeechCalibrationTrait;
        humanValue: number;
        modelValue: number | null;
        calibrationStatus: string;
    }>;
    phoneCandidates: Array<{
        candidateId: string;
        word: string;
        targetPhone: string;
        observedPhone: string;
        startSeconds: number;
        endSeconds: number;
    }>;
};

export type SpeechCalibrationAdjudicationItem = {
    adjudicationId: string;
    assignmentId: string;
    status: string;
    examName: string;
    examTaskType: string;
    questionNo: string | null;
    raterValues: Array<{
        reviewerAlias: string;
        traits: Array<{ trait: SpeechCalibrationTrait; value: number }>;
    }>;
    modelRevision: string;
};

export type SpeechCalibrationAdjudicationList = {
    dateUtc: string;
    page: number;
    pageSize: number;
    total: number;
    items: SpeechCalibrationAdjudicationItem[];
};

export type SpeechCalibrationAdjudication = {
    adjudicationId: string;
    status: string;
    examName: string;
    examTaskType: string;
    questionNo: string | null;
    audioUrl: string;
    referenceText: string | null;
    reasonCodes: string[];
    blindRatings: Array<{
        reviewerAlias: string;
        traits: Array<{
            trait: SpeechCalibrationTrait;
            value: number;
            confidence: SpeechCalibrationConfidence;
        }>;
        flags: string[];
    }>;
    modelValues: Array<{
        trait: SpeechCalibrationTrait;
        value: number | null;
    }>;
    phoneCandidates: Array<{
        candidateId: string;
        word: string;
        targetPhone: string;
        observedPhone: string;
        startSeconds: number;
        endSeconds: number;
    }>;
};

export type SpeechCalibrationReadiness = {
    scope: {
        examName: string | null;
        examTaskType: string | null;
        modelRevision: string | null;
    };
    status: string;
    uniqueRecordings: number;
    independentRatingCoverage: number | null;
    agreement: number | null;
    meanAbsoluteError: number | null;
    p90AbsoluteError: number | null;
    falseCorrectionRate: number | null;
    validAccentFalseAlertRate: number | null;
    subgroups: Array<{ key: string; sampleCount: number; error: number | null }>;
    gates: Array<{
        code: string;
        label: string;
        target: string;
        current: string | null;
        passed: boolean | null;
        mandatory: boolean;
    }>;
    releaseId: string | null;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string | null;
};

export class SpeechCalibrationApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = 'SpeechCalibrationApiError';
        this.status = status;
    }
}

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '');

function getSpeechCalibrationApiRoot() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const configuredBase = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
            'https://test-api.pteultra.com',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const versionedApiRoot = /\/api\/v[^/]+$/i.test(configuredBase)
        ? configuredBase
        : `${configuredBase}/api/v${apiVersion}`;
    const adminToken = String(
        session?.adminToken ??
            session?.accessToken ??
            import.meta.env.VITE_ADMIN_TOKEN ??
            settings.adminApiToken ??
            '',
    );

    return {
        apiRoot: `${versionedApiRoot}/AdminSpeechCalibration`,
        adminToken,
    };
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        const message = record.errorMessage ?? record.message ?? record.error ?? record.title ?? record.detail;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return fallback;
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) return payload as T;
    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Speech calibration request failed'));
    }
    if (envelope.data === undefined) {
        throw new Error('Speech calibration response did not contain data');
    }
    return envelope.data;
}

async function requestJson<T>(
    path: string,
    init: RequestInit = {},
    query: Record<string, string> = {},
): Promise<T> {
    const { apiRoot, adminToken } = getSpeechCalibrationApiRoot();
    const url = new URL(`${apiRoot}/${trimSlashes(path)}`);
    Object.entries(query).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
    });

    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) headers.set('Content-Type', 'application/json');
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);

    const response = await fetch(url.toString(), { ...init, headers });
    const payload = (response.headers.get('content-type') ?? '').includes('application/json')
        ? await response.json()
        : await response.text();
    if (!response.ok) {
        throw new SpeechCalibrationApiError(
            response.status,
            getErrorMessage(payload, `${response.status} ${response.statusText}`),
        );
    }
    return unwrap(payload as T | AdminApiEnvelope<T>);
}

function postJson<T>(path: string, body: unknown) {
    return requestJson<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function createIdempotencyKey(prefix: string) {
    const random = typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}-${random}`;
}

export function getSpeechCalibrationCapabilities() {
    return requestJson<SpeechCalibrationCapabilities>('capabilities');
}

export function ensureSpeechCalibrationDailyQueue(dateUtc: string) {
    return postJson<SpeechCalibrationDailyQueue>(
        `daily-queues/${encodeURIComponent(dateUtc)}/ensure`,
        {},
    );
}

export function getSpeechCalibrationDailyQueue(
    dateUtc: string,
    query: { status?: string; page?: number; pageSize?: number } = {},
) {
    return requestJson<SpeechCalibrationDailyQueue>(
        `daily-queues/${encodeURIComponent(dateUtc)}`,
        {},
        {
            status: query.status ?? '',
            page: String(query.page ?? 1),
            pageSize: String(query.pageSize ?? 100),
        },
    );
}

export function getSpeechCalibrationBlindReview(assignmentId: string) {
    return requestJson<SpeechCalibrationBlindReview>(
        `assignments/${encodeURIComponent(assignmentId)}/blind-review`,
    );
}

export function submitSpeechCalibrationBlindRating(
    assignmentId: string,
    body: {
        traits: Array<{
            trait: SpeechCalibrationTrait;
            value: number;
            confidence: SpeechCalibrationConfidence;
        }>;
        flags: string[];
        notes: string;
        idempotencyKey: string;
    },
) {
    return postJson<{ assignmentId: string; status: string }>(
        `assignments/${encodeURIComponent(assignmentId)}/blind-rating`,
        body,
    );
}

export function getSpeechCalibrationComparison(assignmentId: string) {
    return requestJson<SpeechCalibrationComparison>(
        `assignments/${encodeURIComponent(assignmentId)}/comparison`,
    );
}

export function submitSpeechCalibrationComparison(
    assignmentId: string,
    body: {
        traits: Array<{ trait: SpeechCalibrationTrait; verdict: SpeechCalibrationVerdict }>;
        phones: Array<{ candidateId: string; verdict: SpeechCalibrationPhoneVerdict }>;
        notes: string;
        idempotencyKey: string;
    },
) {
    return postJson<{ assignmentId: string; status: string }>(
        `assignments/${encodeURIComponent(assignmentId)}/comparison`,
        body,
    );
}

export function getSpeechCalibrationAdjudications(query: {
    dateUtc?: string;
    status?: string;
    page?: number;
    pageSize?: number;
}) {
    return requestJson<SpeechCalibrationAdjudicationList>('adjudications', {}, {
        dateUtc: query.dateUtc ?? '',
        status: query.status ?? '',
        page: String(query.page ?? 1),
        pageSize: String(query.pageSize ?? 25),
    });
}

export function getSpeechCalibrationAdjudication(adjudicationId: string) {
    return requestJson<SpeechCalibrationAdjudication>(
        `adjudications/${encodeURIComponent(adjudicationId)}`,
    );
}

export function submitSpeechCalibrationAdjudication(
    adjudicationId: string,
    body: {
        traits: Array<{ trait: SpeechCalibrationTrait; value: number }>;
        phones: Array<{ candidateId: string; verdict: SpeechCalibrationPhoneVerdict }>;
        reasonCodes: string[];
        notes: string;
        idempotencyKey: string;
    },
) {
    return postJson<{ adjudicationId: string; status: string }>(
        `adjudications/${encodeURIComponent(adjudicationId)}`,
        body,
    );
}

export function getSpeechCalibrationReadiness(query: {
    examName?: string;
    examTaskType?: string;
    modelRevision?: string;
}) {
    return requestJson<SpeechCalibrationReadiness>('readiness', {}, {
        examName: query.examName ?? '',
        examTaskType: query.examTaskType ?? '',
        modelRevision: query.modelRevision ?? '',
    });
}

export async function downloadSpeechCalibrationExport(query: {
    fromUtc: string;
    toUtc: string;
    examName?: string;
    examTaskType?: string;
}) {
    const { apiRoot, adminToken } = getSpeechCalibrationApiRoot();
    const url = new URL(`${apiRoot}/exports`);
    url.searchParams.set('fromUtc', query.fromUtc);
    url.searchParams.set('toUtc', query.toUtc);
    url.searchParams.set('format', 'csv');
    if (query.examName) url.searchParams.set('examName', query.examName);
    if (query.examTaskType) url.searchParams.set('examTaskType', query.examTaskType);

    const headers = new Headers({ Accept: 'text/csv' });
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);
    const response = await fetch(url.toString(), { headers });
    if (!response.ok) {
        const payload = (response.headers.get('content-type') ?? '').includes('application/json')
            ? await response.json()
            : await response.text();
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] ?? 'speech-calibration.csv';
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
}
